import React, { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { X, DownloadSimple, ArrowSquareOut, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import { downloadBlob } from "../lib/pdfShare";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

// In-app PDF viewer: fetches the PDF (cookie auth), renders pages with pdf.js (works on mobile too),
// then lets the user download / open it.
export function PdfPreviewModal({ src, filename, title, onClose }) {
  const [blob, setBlob] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [error, setError] = useState("");
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    if (!src) return;
    let cancelled = false;
    let url = null;
    setBlob(null); setPdfDoc(null); setError(""); setBlobUrl(null);
    (async () => {
      try {
        const res = typeof src === "function" ? await src() : await fetch(src, { credentials: "include" });
        if (!res.ok) throw new Error(`PDF generation failed (${res.status})`);
        const b = await res.blob();
        if (cancelled) return;
        const doc = await pdfjsLib.getDocument({ data: await b.arrayBuffer() }).promise;
        if (cancelled) return;
        url = URL.createObjectURL(b);
        setBlob(b); setBlobUrl(url); setPdfDoc(doc);
      } catch (e) {
        if (!cancelled) { setError(e.message || "Could not generate PDF"); toast.error(e.message || "Could not generate PDF"); }
      }
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [src]);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    window.addEventListener("keydown", h, true);
    return () => window.removeEventListener("keydown", h, true);
  }, [onClose]);

  if (!src) return null;
  return (
    <div className="fixed inset-0 bg-ink/70 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose} data-testid="pdf-preview-modal">
      <div className="bg-white rounded-lg w-full max-w-5xl h-[94vh] shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-border">
          <div className="min-w-0">
            <div className="font-display font-semibold truncate" data-testid="pdf-preview-title">{title || "PDF Preview"}</div>
            <div className="text-[11px] text-ink-muted truncate font-mono">{filename}{pdfDoc && ` · ${pdfDoc.numPages} page${pdfDoc.numPages !== 1 ? "s" : ""}`}</div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {blobUrl && <a href={blobUrl} target="_blank" rel="noreferrer" className="btn-secondary inline-flex items-center gap-1 text-xs" data-testid="pdf-open-tab"><ArrowSquareOut size={14} /> <span className="hidden sm:inline">Open in tab</span></a>}
            <button disabled={!blob} onClick={() => { downloadBlob(blob, filename); toast.success("PDF downloaded"); }} className="btn-primary inline-flex items-center gap-1 text-xs" data-testid="pdf-download"><DownloadSimple size={14} weight="bold" /> Download</button>
            <button onClick={onClose} className="btn-ghost" data-testid="pdf-close"><X size={16} /></button>
          </div>
        </div>
        <div className="flex-1 bg-surface-muted relative min-h-0 overflow-y-auto" data-testid="pdf-pages">
          {!pdfDoc && !error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-ink-muted text-sm" data-testid="pdf-loading">
              <CircleNotch size={28} className="animate-spin text-brand" /> Generating PDF…
            </div>
          )}
          {error && <div className="absolute inset-0 flex items-center justify-center text-destructive text-sm" data-testid="pdf-error">{error}</div>}
          {pdfDoc && <PdfPages pdfDoc={pdfDoc} />}
        </div>
      </div>
    </div>
  );
}

// Renders every page of a loaded pdf.js document as canvases (scaled to container width).
export function PdfPages({ pdfDoc }) {
  return Array.from({ length: pdfDoc.numPages }, (_, i) => <PdfPage key={i} pdfDoc={pdfDoc} pageNo={i + 1} />);
}

// Fetches any file URL and previews it inline: images as <img>, PDFs via pdf.js.
export function FilePreview({ url, mime, alt = "Attachment" }) {
  const [state, setState] = useState({ loading: true, error: "", imgUrl: null, pdfDoc: null });
  useEffect(() => {
    if (!url) return;
    let cancelled = false, objUrl = null;
    setState({ loading: true, error: "", imgUrl: null, pdfDoc: null });
    (async () => {
      try {
        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) throw new Error(`Could not load file (${res.status})`);
        const b = await res.blob();
        if (cancelled) return;
        const type = mime || b.type || "";
        if (type.includes("pdf")) {
          const doc = await pdfjsLib.getDocument({ data: await b.arrayBuffer() }).promise;
          if (!cancelled) setState({ loading: false, error: "", imgUrl: null, pdfDoc: doc });
        } else {
          objUrl = URL.createObjectURL(b);
          setState({ loading: false, error: "", imgUrl: objUrl, pdfDoc: null });
        }
      } catch (e) {
        if (!cancelled) setState({ loading: false, error: e.message || "Could not load file", imgUrl: null, pdfDoc: null });
      }
    })();
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [url, mime]);
  if (state.loading) return <div className="flex items-center justify-center gap-2 py-10 text-ink-muted text-xs" data-testid="file-preview-loading"><CircleNotch size={20} className="animate-spin text-brand" /> Loading file…</div>;
  if (state.error) return <div className="py-10 text-center text-destructive text-xs" data-testid="file-preview-error">{state.error}</div>;
  if (state.imgUrl) return <img src={state.imgUrl} alt={alt} className="max-w-full mx-auto rounded shadow" data-testid="file-preview-image" />;
  return <div data-testid="file-preview-pdf"><PdfPages pdfDoc={state.pdfDoc} /></div>;
}

function PdfPage({ pdfDoc, pageNo }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    let task = null;
    (async () => {
      const page = await pdfDoc.getPage(pageNo);
      if (cancelled) return;
      const canvas = canvasRef.current;
      const width = Math.min(wrapRef.current?.clientWidth || 800, 900);
      const base = page.getViewport({ scale: 1 });
      const scale = width / base.width;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const vp = page.getViewport({ scale: scale * dpr });
      canvas.width = vp.width; canvas.height = vp.height;
      canvas.style.width = `${vp.width / dpr}px`; canvas.style.height = `${vp.height / dpr}px`;
      task = page.render({ canvasContext: canvas.getContext("2d"), viewport: vp });
      try { await task.promise; } catch { /* cancelled */ }
    })();
    return () => { cancelled = true; task?.cancel(); };
  }, [pdfDoc, pageNo]);
  return (
    <div ref={wrapRef} className="flex justify-center px-2 py-3" data-testid={`pdf-page-${pageNo}`}>
      <canvas ref={canvasRef} className="shadow-lg bg-white max-w-full" />
    </div>
  );
}
